"use client";

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Collapse,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TuneIcon from "@mui/icons-material/Tune";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import {
  declineMeeting,
  acceptMeeting,
  counterMeeting,
  showAcceptedConfirmation,
  setMeeting,
} from "@/store/apps/meetings/MeetingDataSlice";
import { useRouter } from "next/navigation";

const checkSufficientAvailability = (availData, requiredDuration) => {
  if (!availData?.times || !requiredDuration) return false;

  const requiredMinutes = parseInt(requiredDuration, 10);

  const hasAnyTimeSlots = Object.values(availData.times).some(
    (daySlots) => Array.isArray(daySlots) && daySlots.length > 0,
  );

  if (!hasAnyTimeSlots) return false;

  return Object.values(availData.times).some((daySlots) => {
    if (!Array.isArray(daySlots) || daySlots.length === 0) return false;

    return daySlots.some((slot) => {
      if (
        !slot ||
        !slot.start ||
        !slot.end ||
        !slot.start._seconds ||
        !slot.end._seconds
      ) {
        return false;
      }

      const startTime = slot.start._seconds;
      const endTime = slot.end._seconds;
      const durationSeconds = endTime - startTime;
      const durationMinutes = durationSeconds / 60;

      return durationMinutes >= requiredMinutes;
    });
  });
};

const ReceiveModal = ({
  open,
  handleClose,
  content,
  fromAvailabilityPage = false,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const router = useRouter();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasSufficientAvailability, setHasSufficientAvailability] = useState(
    false,
  );
  const [seekerName, setSeekerName] = useState("");
  const [showRateAdjust, setShowRateAdjust] = useState(false);
  const [rateInputStr, setRateInputStr] = useState("");
  const [adjustedRate, setAdjustedRate] = useState(null);
  const [showGoalDialog, setShowGoalDialog] = useState(false);

  const currentMeetings = useSelector((state) => state.meetings.data);
  const availData = useSelector((state) => state.availability.data);

  const availability =
    availData?.available &&
    availData?.times &&
    Object.values(availData.times).some(
      (daySlots) => Array.isArray(daySlots) && daySlots.length > 0,
    );

  const duration = content?.session?.duration;
  const rate = content?.session?.session_rate;
  const goal = (
    content?.session?.goal_enhanced ||
    content?.session?.goal_raw ||
    ""
  )
    .replace(/^"|"$/g, "")
    .replace(/\b(Dr|Prof|Mr|Mrs|Ms)\.?\s+/gi, "")
    .trim();

  const requestId = content?.request_id || content?.requestId;
  const sharerId = content?.sharer_id || content?.sharerId;
  const name = (content?.seeker?.given_name || "").replace(
    /\b(Dr|Prof|Mr|Mrs|Ms)\.?\s+/gi,
    "",
  );
  const lastName = (content?.seeker?.family_name || "").replace(
    /\b(Dr|Prof|Mr|Mrs|Ms)\.?\s+/gi,
    "",
  );
  const fullName = [name, lastName].filter(Boolean).join(" ") || "Someone";

  const expiry = 7;

  useEffect(() => {
    if (content?.seeker?.given_name) {
      setSeekerName(content.seeker.given_name);
    }
  }, [content]);

  useEffect(() => {
    if (availData && duration) {
      const sufficient = checkSufficientAvailability(availData, duration);
      setHasSufficientAvailability(sufficient);
    }
  }, [availData, duration]);

  useEffect(() => {
    if (open) {
      const newUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    } else {
      setShowRateAdjust(false);
      setRateInputStr("");
      setAdjustedRate(null);
      setShowGoalDialog(false);
    }
  }, [open]);

  const GOAL_PREVIEW_WORDS = 14;
  const goalWords = goal ? goal.split(/\s+/).filter(Boolean) : [];
  const isLongGoal = goalWords.length > GOAL_PREVIEW_WORDS;
  const shouldTruncateGoal = isLongGoal;
  const goalPreview = isLongGoal
    ? `${goalWords.slice(0, GOAL_PREVIEW_WORDS).join(" ")}...`
    : goal;

  const handleAccept = async () => {
    if (isSubmitted) return;
    setIsSubmitted(true);
    setSeekerName(name);

    const currentMeeting = currentMeetings.find((m) => m.id === requestId);

    if (currentMeeting) {
      const now = new Date().toISOString();

      dispatch(
        setMeeting({
          ...currentMeeting,
          status: "accepted",
          status_time: now,
          status_log: [
            ...(currentMeeting.status_log || []),
            {
              status: "accepted",
              time: now,
            },
          ],
          session: {
            ...currentMeeting.session,
            ...(adjustedRate !== null &&
              adjustedRate !== rate && {
                session_rate: adjustedRate,
                original_session_rate: rate,
                rate_adjusted_by_sharer: true,
              }),
          },
        }),
      );
    }

    await dispatch(
      acceptMeeting({
        requestId,
        sharerId,
        ...(adjustedRate !== null && adjustedRate !== rate && { adjustedRate }),
      }),
    ).unwrap();

    handleClose();
    dispatch(showAcceptedConfirmation(seekerName || name));

    if (fromAvailabilityPage) {
      router.push("/");
    }
  };

  const handleCounter = async () => {
    if (isSubmitted) return;
    setIsSubmitted(true);

    const currentMeeting = currentMeetings.find((m) => m.id === requestId);

    if (currentMeeting) {
      const now = new Date().toISOString();
      dispatch(
        setMeeting({
          ...currentMeeting,
          status: "countered",
          status_time: now,
          status_log: [
            ...(currentMeeting.status_log || []),
            { status: "countered", time: now },
          ],
          session: {
            ...currentMeeting.session,
            proposed_rate: adjustedRate,
            original_session_rate: rate,
            rate_countered_by_sharer: true,
          },
        }),
      );
    }

    await dispatch(
      counterMeeting({ requestId, sharerId, proposedRate: adjustedRate }),
    ).unwrap();

    handleClose();
    dispatch(showAcceptedConfirmation(seekerName || name));

    if (fromAvailabilityPage) {
      router.push("/");
    }
  };

  const handleDecline = async () => {
    if (isSubmitted) return;
    setIsSubmitted(true);

    const currentMeeting = currentMeetings.find((m) => m.id === requestId);

    if (currentMeeting) {
      const now = new Date().toISOString();

      dispatch(
        setMeeting({
          ...currentMeeting,
          status: "declined",
          status_time: now,
          status_log: [
            ...(currentMeeting.status_log || []),
            {
              status: "declined",
              time: now,
            },
          ],
        }),
      );
    }

    await dispatch(declineMeeting({ requestId, sharerId })).unwrap();
    handleClose();
  };

  const handleSetupAvailability = () => {
    handleClose();
    if (fromAvailabilityPage) {
      handleClose();
    } else {
      router.push("/availability");
    }
  };

  const handleRateInput = (e) => {
    const str = e.target.value;
    const n = parseFloat(str);

    if (!Number.isNaN(n)) {
      // Clamp: max $50,000, min $15 (0 is allowed and means waiving the fee).
      let clamped = Math.round(n);
      if (clamped > 50000) clamped = 50000;
      else if (clamped > 0 && clamped < 15) clamped = 15;

      setRateInputStr(String(clamped));
      setAdjustedRate(clamped);
    } else {
      setRateInputStr(str);
      setAdjustedRate(null);
    }
  };

  const createBodyContent = () => {
    const factRow = (icon, text) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          py: 0.35,
        }}
      >
        <Box
          sx={{
            width: 20,
            height: 20,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 400, fontSize: "0.9rem", color: "text.primary" }}
        >
          {text}
        </Typography>
      </Box>
    );

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <Box
          sx={{
            pb: 2.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              columnGap: 3,
              rowGap: 1,
            }}
          >
            {factRow(
              <AccessTimeIcon sx={{ fontSize: 18 }} />,
              `${duration} minutes`,
            )}
            {rate > 0 &&
              factRow(
                <PaymentsOutlinedIcon
                  sx={{ fontSize: 18 }}
                />,
                `$${rate} USD for your time`,
              )}
            {factRow(
              <EventOutlinedIcon
                sx={{ fontSize: 18 }}
              />,
              `Respond within ${expiry} days`,
            )}
          </Box>
        </Box>

        {goal && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.primary",
                fontWeight: 600,
              }}
            >
              About this meeting
            </Typography>
            <Typography
              variant="body2"
              onClick={() => {
                if (shouldTruncateGoal) setShowGoalDialog(true);
              }}
              sx={{
                mt: 1,
                color: "text.primary",
                lineHeight: 1.55,
                fontSize: "1rem",
                cursor: shouldTruncateGoal ? "pointer" : "default",
              }}
            >
              {shouldTruncateGoal ? goalPreview : goal}
            </Typography>
            {shouldTruncateGoal && (
              <Typography
                variant="caption"
                sx={{
                  mt: 0.7,
                  color: "primary.main",
                  display: "inline-block",
                  cursor: "pointer",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                }}
                onClick={() => setShowGoalDialog(true)}
              >
                Read full goal
              </Typography>
            )}
          </Box>
        )}

        {availability && !hasSufficientAvailability ? (
          <Box
            sx={{
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Your available time slots are not long enough for this {duration}
              -minute meeting. Add longer continuous blocks of{" "}
              <Box
                component="span"
                onClick={handleSetupAvailability}
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  cursor: "pointer",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                  "&:hover": { color: "warning.dark" },
                }}
              >
                availability
              </Box>
              .
            </Typography>
          </Box>
        ) : !availability ? (
          <Box
            sx={{
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Make sure your{" "}
              <Box
                component="span"
                onClick={handleSetupAvailability}
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                  cursor: "pointer",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                  "&:hover": { color: "primary.main" },
                }}
              >
                availability
              </Box>{" "}
              is up to date so they can find a time that works for you.
            </Typography>
          </Box>
        ) : null}

        {rate > 0 && hasSufficientAvailability && (
          <Box>
            <Box
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() => {
                  if (showRateAdjust) {
                    setRateInputStr("");
                    setAdjustedRate(null);
                  }
                  setShowRateAdjust((v) => !v);
                }}
                sx={{
                  px: 0,
                  py: 1.4,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <TuneIcon
                    sx={{
                      color: "text.secondary",
                      fontSize: 20,
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      lineHeight: 1.25,
                      color: "text.primary",
                    }}
                  >
                    Adjust the rate
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.35,
                    }}
                  >
                    Lower, waive, or adjust the amount
                  </Typography>
                </Box>

                <Box sx={{ flexShrink: 0, color: "text.disabled" }}>
                  {showRateAdjust ? (
                    <ExpandLessIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 20 }} />
                  )}
                </Box>
              </Box>

              <Collapse in={showRateAdjust}>
                <Box
                  sx={{
                    px: 2,
                    pt: 2.75,
                    pb: 0,
                    borderTop: "1px dashed",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      flexWrap: "wrap",
                    }}
                  >
                    <TextField
                      size="small"
                      type="number"
                      value={rateInputStr}
                      onChange={handleRateInput}
                      label="New amount"
                      placeholder=""
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{
                                "& .MuiTypography-root": { fontSize: "0.8rem" },
                              }}
                            >
                              $
                            </InputAdornment>
                          ),
                          inputProps: { min: 0, max: 50000, step: 1 },
                        },
                      }}
                      sx={{
                        width: 132,
                        "& .MuiInputBase-input": { fontSize: "0.84rem" },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "text.disabled", flexShrink: 0 }}
                    >
                      or
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        setRateInputStr("0");
                        setAdjustedRate(0);
                      }}
                      sx={{
                        textTransform: "none",
                        fontSize: "0.82rem",
                        p: 0,
                        minWidth: 0,
                        "&:hover": { bgcolor: "transparent" },
                      }}
                    >
                      Waive the fee
                    </Button>
                  </Box>

                  <Box sx={{ minHeight: 20, mt: 0.65, pb: 0.8 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          rateInputStr !== "" && adjustedRate === null
                            ? "error.main"
                            : "text.secondary",
                      }}
                    >
                      {rateInputStr !== "" && adjustedRate === null
                        ? "Enter a valid amount"
                        : adjustedRate !== null && adjustedRate > rate
                        ? `Seeker will be asked to approve $${adjustedRate}`
                        : adjustedRate !== null && adjustedRate === 0
                        ? "Fee fully waived - this meeting will be free"
                        : adjustedRate !== null && adjustedRate === rate
                        ? ""
                        : adjustedRate !== null
                        ? `Reduced to $${adjustedRate} (from $${rate})`
                        : ""}
                    </Typography>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Box>
        )}

        {hasSufficientAvailability && availability && (
          <Typography
            variant="caption"
            sx={{ color: "text.disabled", display: "block" }}
          >
            Consider updating your{" "}
            <Box
              component="span"
              onClick={handleSetupAvailability}
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                textUnderlineOffset: "2px",
                "&:hover": { color: "text.secondary" },
              }}
            >
              availability
            </Box>{" "}
            to give them more times to choose from.
          </Typography>
        )}
      </Box>
    );
  };

  const getActionConfig = () => {
    if (!availability) {
      return {
        confirmButtonText: fromAvailabilityPage
          ? "Set Availability"
          : "Set Availability to Proceed",
        onConfirm: handleSetupAvailability,
        showIgnoreButton: true,
        ignoreButtonText: "Decline",
        onIgnore: handleDecline,
      };
    }

    if (!hasSufficientAvailability) {
      return {
        confirmButtonText: fromAvailabilityPage
          ? "Add Longer Blocks"
          : "Set Longer Availability Blocks",
        onConfirm: handleSetupAvailability,
        showIgnoreButton: true,
        ignoreButtonText: "Decline",
        onIgnore: handleDecline,
      };
    }

    const isCounterOffer = adjustedRate !== null && adjustedRate > rate;
    const hasDecrease =
      adjustedRate !== null && adjustedRate !== rate && adjustedRate <= rate;

    return {
      confirmButtonText: isCounterOffer
        ? `Counter-offer ($${adjustedRate})`
        : hasDecrease
        ? adjustedRate === 0
          ? "Accept (No fee)"
          : `Accept ($${adjustedRate})`
        : "Accept",
      onConfirm: isCounterOffer ? handleCounter : handleAccept,
      showIgnoreButton: true,
      ignoreButtonText: "Decline",
      onIgnore: handleDecline,
    };
  };

  const actionConfig = getActionConfig();

  return (
    <>
      <ConfirmDialog
        open={open}
        onClose={handleClose}
        title={`Meeting request from ${name || fullName}`}
        body={createBodyContent()}
        onConfirm={actionConfig.onConfirm}
        onIgnore={actionConfig.onIgnore}
        confirmButtonText={actionConfig.confirmButtonText}
        ignoreButtonText={actionConfig.ignoreButtonText}
        showConfirmButton={true}
        showIgnoreButton={actionConfig.showIgnoreButton}
        showCloseIcon={true}
        isProcessing={isSubmitted}
        processingText="Processing..."
        modalStyle="center"
        backdropStyle="frosted"
        minHeight="auto"
        confirmButtonVariant="contained"
        confirmButtonSx={{
          color: theme.palette.primary.contrastText,
          boxShadow: "none",
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: "none",
          },
        }}
        ignoreButtonSx={{ color: "text.secondary" }}
        disableConfirm={rateInputStr !== "" && adjustedRate === null}
        disableBackdropClick={false}
        disableEscapeKeyDown={false}
      />

      <Dialog
        open={showGoalDialog}
        onClose={() => setShowGoalDialog(false)}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              borderRadius: "14px",
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            pb: 0.5,
            pr: 5,
          }}
        >
          Meeting goal
          <IconButton
            aria-label="Close"
            onClick={() => setShowGoalDialog(false)}
            size="small"
            sx={{
              position: "absolute",
              right: 10,
              top: 10,
              color: "text.secondary",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important", pb: 2.2 }}>
          <Box
            sx={{
              px: 1.2,
              py: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.88rem",
                lineHeight: 1.45,
                color: "text.primary",
                fontStyle: "italic",
              }}
            >
              {goal}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReceiveModal;
